import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createCompanyUser,
  fetchCompanyUsers,
  updateCompanyUserStatus,
  assignUserLoginCode,
  regenerateUserLoginCode,
  fetchUserRoleMetrics,
} from '../../services/userManagementService';
import {
  CreateCompanyUserRequest,
  ManagedUser,
  UpdateCompanyUserStatusRequest,
  UserRoleMetric,
} from '../../types/userManagement';

export const fetchCompanyUsersThunk = createAsyncThunk<
  ManagedUser[],
  string | null | undefined,
  { rejectValue: string }
>('users/fetchCompanyUsers', async (outletId, { rejectWithValue }) => {
  try {
    const response = await fetchCompanyUsers(outletId);
    if (!response.success) {
      return rejectWithValue(
        response.error || 'Failed to fetch employee accounts',
      );
    }

    return response.data || [];
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to fetch employee accounts',
    );
  }
});

export const createCompanyUserThunk = createAsyncThunk<
  ManagedUser,
  CreateCompanyUserRequest,
  { rejectValue: string }
>('users/createCompanyUser', async (request, { rejectWithValue }) => {
  try {
    const response = await createCompanyUser(request);
    if (!response.success || !response.data) {
      return rejectWithValue(
        response.error || 'Failed to create employee account',
      );
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to create employee account',
    );
  }
});

export const updateCompanyUserStatusThunk = createAsyncThunk<
  ManagedUser,
  UpdateCompanyUserStatusRequest,
  { rejectValue: string }
>('users/updateCompanyUserStatus', async (request, { rejectWithValue }) => {
  try {
    const response = await updateCompanyUserStatus(request);
    if (!response.success || !response.data) {
      return rejectWithValue(
        response.error || 'Failed to update employee status',
      );
    }

    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to update employee status',
    );
  }
});

export const assignUserLoginCodeThunk = createAsyncThunk<
  ManagedUser,
  { userId: string; loginCode: string | null },
  { rejectValue: string }
>(
  'users/assignUserLoginCode',
  async ({ userId, loginCode }, { rejectWithValue }) => {
    try {
      const response = await assignUserLoginCode(userId, loginCode);
      if (!response.success || !response.data) {
        return rejectWithValue(
          response.error || 'Failed to update employee passcode',
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to update employee passcode',
      );
    }
  },
);

export const regenerateUserLoginCodeThunk = createAsyncThunk<
  ManagedUser,
  string,
  { rejectValue: string }
>('users/regenerateUserLoginCode', async (userId, { rejectWithValue }) => {
  try {
    const response = await regenerateUserLoginCode(userId);
    if (!response.success || !response.data) {
      return rejectWithValue(
        response.error || 'Failed to regenerate employee passcode',
      );
    }
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Failed to regenerate employee passcode',
    );
  }
});

export const fetchUserRoleMetricsThunk = createAsyncThunk<
  UserRoleMetric[],
  string | null | undefined,
  { rejectValue: string }
>('users/fetchUserRoleMetrics', async (outletId, { rejectWithValue }) => {
  try {
    const response = await fetchUserRoleMetrics(outletId);
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch user metrics');
    }

    return response.data || [];
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch user metrics',
    );
  }
});
